import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { LignesFacadeService } from "../../../ligne/services/lignes/lignes-facade.service";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { AideContextComponent } from "../../../../shared/components/aide-context/aide-context.component";
import { AideContextSection } from "../../../../shared/models/aide-context.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { format } from "date-fns";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { OF } from "../../../of/models/of.model";

@Component({
  selector: "vl-widget-productivite-ligne",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, SharedModule, AideContextComponent, ChipsToggleComponent, LoaderSkeletonComponent],
  templateUrl: "./widget-productivite-ligne.component.html",
  styleUrls: ["./widget-productivite-ligne.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "widget-component" },
})
export class WidgetProductiviteLigneComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  private ligneFacade = inject(LignesFacadeService);
  private articleInfosFacade = inject(ArticlesInfosFacadeService);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  stats = this.ligneFacade.getState().stats;

  noData = computed(() => this.stats() !== null && !this.chartData().labels.length);

  aideSections: AideContextSection[] = [
    { type: "text", html: TranslateUtils.translate("stats_ligne.aide_productivite") },
    { type: "highlight", title: TranslateUtils.translate("stats_ligne.calcul"), html: TranslateUtils.translate("stats_ligne.aide_productivite_formule") },
  ];

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("prodChart");
  chart?: Chart;

  selectedUnite = signal<string>("ALL");

  /** Map articleId → unite denomination, rebuilt when stats change */
  private uniteMap = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    const map = new Map<string, string>();
    for (const of of ofs) {
      const articleId = of.articleId;
      if (!articleId || map.has(articleId)) continue;
      const denom = this.articleInfosFacade.getByArticleId(articleId)?.()?.unite?.denomination ?? "";
      map.set(articleId, denom);
    }
    return map;
  });

  uniteElements = computed(() => {
    const unites = new Set(this.uniteMap().values());
    unites.delete("");
    const sorted = [...unites].sort();
    return [
      { name: TranslateUtils.translate("stats_ligne.toutes"), value: "ALL" },
      ...sorted.map((u) => ({ name: u, value: u })),
    ];
  });

  private static UNITE_COLORS = [
    "hsl(210 70% 55%)", "hsl(35 90% 55%)", "hsl(140 70% 45%)",
    "hsl(280 60% 55%)", "hsl(0 70% 55%)", "hsl(170 60% 45%)",
    "hsl(45 90% 50%)", "hsl(320 60% 55%)",
  ];

  private static scoreColor(avg: number): string {
    if (avg >= 95) return "hsl(140 70% 45%)";
    if (avg >= 85) return "hsl(35 90% 55%)";
    return "hsl(0 70% 55%)";
  }

  chartData = computed(() => {
    const allOfs: OF[] = this.stats()?.ofs ?? [];
    const unite = this.selectedUnite();
    const uMap = this.uniteMap();

    if (unite !== "ALL") {
      return this.buildSingleUniteData(allOfs.filter((of) => uMap.get(of.articleId) === unite));
    }

    // Mode "Toutes" → un dataset par unité
    const unites = [...new Set([...uMap.values()].filter(Boolean))].sort();
    if (!unites.length) return { mode: "multi" as const, labels: [] as string[], datasets: [] as { label: string; data: (number | null)[]; backgroundColor: string; borderRadius: number }[] };

    // Collect all month keys
    const monthSet = new Set<string>();
    for (const of of allOfs) {
      if (!of.dateFin || of.scoreTotal == null) continue;
      monthSet.add(format(new Date(of.dateFin), "yyyy-MM"));
    }
    const months = [...monthSet].sort();
    if (!months.length) return { mode: "multi" as const, labels: [] as string[], datasets: [] as { label: string; data: (number | null)[]; backgroundColor: string; borderRadius: number }[] };

    const datasets = unites.map((u, i) => {
      const grouped = new Map<string, { weightedSum: number; totalQty: number }>();
      for (const of of allOfs) {
        if (!of.dateFin || of.scoreTotal == null || uMap.get(of.articleId) !== u) continue;
        const qty = parseNumber(of.quantiteProduite ?? 0) || 0;
        const key = format(new Date(of.dateFin), "yyyy-MM");
        const entry = grouped.get(key) ?? { weightedSum: 0, totalQty: 0 };
        entry.weightedSum += parseNumber(of.scoreTotal) * qty;
        entry.totalQty += qty;
        grouped.set(key, entry);
      }
      return {
        label: u,
        data: months.map((m) => {
          const e = grouped.get(m);
          return e && e.totalQty > 0 ? Math.round((e.weightedSum / e.totalQty) * 10) / 10 : null;
        }),
        backgroundColor: WidgetProductiviteLigneComponent.UNITE_COLORS[i % WidgetProductiviteLigneComponent.UNITE_COLORS.length],
        borderRadius: 4,
      };
    });

    return {
      mode: "multi" as const,
      labels: months.map((m) => this.formatLabel(m)),
      datasets,
    };
  });

  private buildSingleUniteData(ofs: OF[]) {
    if (!ofs.length) return { mode: "single" as const, labels: [] as string[], values: [] as number[], colors: [] as string[] };

    const grouped = new Map<string, { weightedSum: number; totalQty: number }>();
    for (const of of ofs) {
      if (!of.dateFin || of.scoreTotal == null) continue;
      const qty = parseNumber(of.quantiteProduite ?? 0) || 0;
      const key = format(new Date(of.dateFin), "yyyy-MM");
      const entry = grouped.get(key) ?? { weightedSum: 0, totalQty: 0 };
      entry.weightedSum += parseNumber(of.scoreTotal) * qty;
      entry.totalQty += qty;
      grouped.set(key, entry);
    }

    const entries = [...grouped.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => ({ key: k, avg: v.totalQty > 0 ? v.weightedSum / v.totalQty : 0 }));

    return {
      mode: "single" as const,
      labels: entries.map((e) => this.formatLabel(e.key)),
      values: entries.map((e) => Math.round(e.avg * 10) / 10),
      colors: entries.map((e) => WidgetProductiviteLigneComponent.scoreColor(e.avg)),
    };
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.destroyChart());

    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private formatLabel(key: string): string {
    const m = key.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${WidgetHelperService.MONTHS_SHORT[parseInt(m[2], 10) - 1]} ${m[1].slice(2)}`;
    return key;
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(data: any) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const textColor =
      getComputedStyle(this.elementRef.nativeElement)
        .getPropertyValue("--color-on-background-alt")
        .trim() || "#888";

    const isMulti = data.mode === "multi";

    const datasets = isMulti
      ? data.datasets
      : [
          {
            label: TranslateUtils.translate("stats_ligne.score_moyen"),
            data: data.values,
            backgroundColor: data.colors,
            borderRadius: 4,
          },
        ];

    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: { labels: data.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: isMulti,
            position: "bottom",
            labels: { color: textColor, boxWidth: 12, padding: 8, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${isMulti ? ctx.dataset.label + ": " : "Score: "}${ctx.parsed.y}%`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, maxRotation: 45, font: { size: 10 } },
          },
          y: {
            beginAtZero: true,
            suggestedMax: 110,
            ticks: {
              color: textColor,
              font: { size: 10 },
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    };
    this.chart = new Chart(ctx, config);
  }
}
